import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiPhone, FiTool, FiRocket, FiArrowRight } = FiIcons;

const HowItWorksSection = () => {
  const handleCTAClick = () => {
    window.open('https://app.noem.ai/cb/growthshop', '_blank');
  };

  const steps = [
    {
      number: "01",
      icon: FiPhone,
      title: "Tell Us About Your Business",
      description: "We have a quick 15-minute call to understand your store, your products, and your goals.",
      time: "15 minutes of your time",
      color: "from-blue-500 to-cyan-500"
    },
    {
      number: "02",
      icon: FiTool,
      title: "We Build Your AI",
      description: "Our expert partners at Noem AI build and train a custom AI assistant on your specific data. We handle all the technical work.",
      time: "We do 100% of the work",
      color: "from-purple-500 to-pink-500"
    },
    {
      number: "03",
      icon: FiRocket,
      title: "Launch & Convert",
      description: "We deploy the AI to your website. It starts recovering abandoned carts and closing sales from day one.",
      time: "Results from day 1",
      color: "from-emerald-500 to-teal-500"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-6xl font-black text-gray-900 mb-6">
            Your Custom AI Sales Assistant,{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-500">
              Live in 7 Days.
            </span>
          </h2>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Simple 3-step process. We do all the heavy lifting.
          </p>
        </motion.div>

        <div className="max-w-5xl mx-auto space-y-8">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100 hover:shadow-2xl transition-all duration-300"
            >
              <div className="flex flex-col md:flex-row items-start gap-6">
                {/* Step Number & Icon */}
                <div className="flex items-center gap-4">
                  <div className={`w-16 h-16 bg-gradient-to-r ${step.color} rounded-2xl flex items-center justify-center text-white font-black text-2xl flex-shrink-0`}>
                    {step.number}
                  </div>
                  <div className={`w-16 h-16 bg-gradient-to-r ${step.color} bg-opacity-10 rounded-2xl flex items-center justify-center flex-shrink-0`}>
                    <SafeIcon icon={step.icon} className="text-3xl" style={{ color: step.color.split(' ')[1] }} />
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1">
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-lg text-gray-600 leading-relaxed mb-3">
                    {step.description}
                  </p>
                  <div className={`inline-flex items-center gap-2 bg-gradient-to-r ${step.color} bg-opacity-10 rounded-full px-4 py-2`}>
                    <div className={`w-2 h-2 bg-gradient-to-r ${step.color} rounded-full`}></div>
                    <span className="text-sm font-bold text-gray-700">{step.time}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center mt-12"
        >
          <motion.button
            onClick={handleCTAClick}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-black py-4 px-8 rounded-2xl text-lg shadow-2xl flex items-center gap-2 mx-auto cursor-pointer"
          >
            <span>GET STARTED - BOOK YOUR CALL</span>
            <SafeIcon icon={FiArrowRight} className="text-xl" />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorksSection;